'use client';

import { useTranslations } from 'next-intl';
import { useViewerTimezone } from '@/hooks/use-local-time-range';
import { repeatingTimeWithDay } from '@/lib/scheduling/repeating-time-label';
import type { SpeakingLesson } from '@/lib/scheduling/speaking-lesson';
import type { ClassMeetingCount } from '@/lib/enrollment/meeting-proration';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * What `onChange` reports for "None of these times work for us".
 *
 * ⚠️ UI-ONLY. It is never a section id and must never reach the server: a host
 * form that sees it sends a `wantsAnotherTime` request naming NO class instead
 * (`AnotherTimeRequest`), and refuses it anywhere a real class is expected.
 */
export const NONE_OF_THESE_TIMES = '__none_of_these_times__';

export type PickableClass = {
  /** The section id to submit. For a multi-leg class, its first leg. */
  sectionId: string;
  /**
   * Every meeting of this class — one for weekly, two for twice-weekly.
   *
   * `speakingLesson` is the lesson THIS LEG's line speaks for (EIFO report #198,
   * issue #766), off `ApprovableClass.legs[].speakingLesson`. Per leg, never per
   * class: on a transition week a twice-weekly class's two meetings can sit on
   * opposite sides of the clock change. Part 4 reads it.
   */
  legs: Array<{
    id: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    speakingLesson?: SpeakingLesson | null;
  }>;
  places: number;
  free: number;
  isFull: boolean;
  hasUnassignedTeacher: boolean;
  isShortRun: boolean;
  /** The class's real meetings — what joining it mid-term is priced by. */
  meetings?: ClassMeetingCount;
  startDate: string | null;
  endDate: string | null;
};

export type ClassPickerProps = {
  classes: PickableClass[];
  value: string;
  onChange: (sectionId: string) => void;
  /**
   * What choosing a class MEANS here, and therefore whether a full one can be
   * chosen:
   *
   *   `queue` — "put my child on this class's list". A full class is the whole
   *             point: the parent is queueing precisely because there is no
   *             room. Full classes stay selectable and are labelled full.
   *   `book`  — "take this seat now, and pay". A full class cannot be taken.
   *
   * ⚠️ This distinction is load-bearing, not cosmetic. The mid-term picker
   * appears on the `waitlist` state, and that state means `availableSeats <= 0`
   * for the WHOLE (course, term) — so every class reads full. A picker that
   * disabled full classes would let the parent choose nothing at all, leave
   * `suggested_section_id` null, and hand the waitlist entry no class to name.
   * Making full classes unselectable here does not tighten the UI; it deletes
   * the feature.
   */
  mode: 'queue' | 'book';
  disabled?: boolean;
  /**
   * Adds "None of these times work for us" as the LAST choice, for a family who
   * can attend none of the listed classes. It reports `NONE_OF_THESE_TIMES` and
   * is selectable in either mode — it takes no seat, so "full" never applies.
   */
  allowNone?: boolean;
};

/**
 * "Which of these classes suits you?" — the question that replaced a weekly
 * availability grid on the mid-term paths.
 *
 * The grid asked families when they were free so a matching run could build
 * classes around the answers. Mid-term the matching run has already happened;
 * the classes exist, at fixed times, and the only real question is which one.
 *
 * Presentational and standalone on purpose: Move 2b mounts this same component
 * on the join rail (`MidTermBackfillForm`) in `book` mode, so the two surfaces
 * cannot drift into showing the same classes differently.
 */
export function ClassPicker({
  classes,
  value,
  onChange,
  mode,
  disabled,
  allowNone,
}: ClassPickerProps) {
  const t = useTranslations('public.enrollment.form');
  const viewerTz = useViewerTimezone();

  // A class nobody teaches cannot be joined or queued for — `matching_placements`
  // requires a teacher, so it is not an option in either mode.
  const options = classes.filter((c) => !c.hasUnassignedTeacher);
  if (options.length === 0) return null;

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="block text-sm font-medium text-gray-700 mb-1">{t('chooseClass')}</legend>
      {mode === 'queue' && <p className="text-xs text-[#6b7280] mb-2">{t('chooseClassHint')}</p>}

      <div className="space-y-2" role="radiogroup" aria-label={t('chooseClass')}>
        {options.map((c) => {
          // In `queue` mode a full class is still a legitimate choice.
          const selectable = mode === 'queue' || !c.isFull;
          const checked = value === c.sectionId;
          const times = c.legs
            .map((leg) =>
              repeatingTimeWithDay(
                {
                  slot: leg,
                  // `class_sections` stores its weekly triple in UTC.
                  frame: 'utc',
                  readerTz: viewerTz,
                  speaking: leg.speakingLesson,
                },
                DAYS
              )
            )
            .join(' + ');

          return (
            <label
              key={c.sectionId}
              data-testid="class-option"
              data-section-id={c.sectionId}
              className={`flex items-start gap-3 rounded-lg border p-3 ${
                checked ? 'border-eifo-orange bg-orange-50' : 'border-gray-200'
              } ${selectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
            >
              <input
                type="radio"
                name="class-choice"
                className="mt-1"
                value={c.sectionId}
                checked={checked}
                disabled={!selectable}
                onChange={() => onChange(c.sectionId)}
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-[#0f172a]">
                  {c.isShortRun && c.startDate
                    ? `${c.startDate}${c.endDate ? ` → ${c.endDate}` : ''} · ${times}`
                    : times}
                </span>
                <span className="block text-xs text-[#6b7280] mt-0.5">
                  {c.isFull
                    ? t('classFull')
                    : t('classSeatsFree', { free: c.free, total: c.places })}
                </span>
              </span>
            </label>
          );
        })}

        {/* Same radio group as the classes: choosing it un-chooses them. */}
        {allowNone && (
          <label
            data-testid="class-option-none"
            className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
              value === NONE_OF_THESE_TIMES ? 'border-eifo-orange bg-orange-50' : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              name="class-choice"
              className="mt-1"
              value={NONE_OF_THESE_TIMES}
              checked={value === NONE_OF_THESE_TIMES}
              onChange={() => onChange(NONE_OF_THESE_TIMES)}
            />
            <span className="flex-1">
              <span className="block text-sm font-medium text-[#0f172a]">
                {t('noneOfTheseTimes')}
              </span>
              <span className="block text-xs text-[#6b7280] mt-0.5">
                {t('noneOfTheseTimesHint')}
              </span>
            </span>
          </label>
        )}
      </div>
    </fieldset>
  );
}
